import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useAuth from '../contexts/useAuth';
import type { ParsedSentence } from '../../../types';

const Sentences = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [sentences, setSentences] = useState<ParsedSentence[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');

  // Redirect non-admins
  useEffect(() => {
    if (!authLoading && (!user || !user.isAdmin)) {
      navigate('/');
    }
  }, [authLoading, user, navigate]);

  // Fetch content title
  useEffect(() => {
    if (!id) return;
    fetch(`/api/content/${id}`, { credentials: 'include' })
      .then((res) => res.json())
      .then((data) => setTitle(data.title || ''))
      .catch(() => {});
  }, [id]);

  // Fetch all sentences
  useEffect(() => {
    if (!id || !user?.isAdmin) return;

    const fetchAllSentences = async () => {
      setLoading(true);
      try {
        // First fetch to get total count
        const initialRes = await fetch(`/api/sentences/${id}?start=0&end=100`, {
          credentials: 'include',
        });
        if (!initialRes.ok) throw new Error('Failed to fetch');

        const initialData = await initialRes.json();
        setTotal(initialData.total);

        // If we have all, we're done
        if (initialData.total <= 100) {
          setSentences(initialData.sentences);
          setLoading(false);
          return;
        }

        // Otherwise fetch the rest
        const allSentences: ParsedSentence[] = [...initialData.sentences];
        let start = 100;

        while (start < initialData.total) {
          const res = await fetch(
            `/api/sentences/${id}?start=${start}&end=${Math.min(start + 100, initialData.total)}`,
            { credentials: 'include' }
          );
          if (res.ok) {
            const data = await res.json();
            allSentences.push(...data.sentences);
          }
          start += 100;
        }

        setSentences(allSentences.sort((a, b) => a.idx - b.idx));
      } catch (err) {
        console.error('Failed to fetch sentences', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllSentences();
  }, [id, user?.isAdmin]);

  if (authLoading || !user?.isAdmin) {
    return <div className="page" />;
  }

  return (
    <div
      className="page"
      style={{
        padding: '20px',
        overflowY: 'auto',
      }}
    >
      <h2 style={{ marginBottom: '8px' }}>{title || 'Sentences'}</h2>
      <p style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>
        {loading ? 'Loading...' : `${total} sentences`}
      </p>

      {!loading && sentences.length === 0 && (
        <p style={{ color: 'var(--text-secondary)' }}>No sentences found</p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {sentences.map((sentence) => (
          <div
            key={sentence.idx}
            style={{
              padding: '12px',
              background: 'var(--surface)',
              borderRadius: '8px',
              border: '1px solid var(--border)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '4px',
                fontSize: '0.75rem',
                color: 'var(--text-secondary)',
              }}
            >
              <span>#{sentence.idx}</span>
              <span>
                {sentence.heading && (
                  <span
                    style={{
                      background: 'var(--primary)',
                      color: 'white',
                      padding: '1px 6px',
                      borderRadius: '4px',
                      marginRight: '8px',
                    }}
                  >
                    heading
                  </span>
                )}
                {sentence.seconds.toFixed(2)}s
              </span>
            </div>
            <p
              style={{
                margin: 0,
                fontWeight: sentence.heading ? 600 : 400,
              }}
            >
              {sentence.text}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Sentences;
